import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

// Jira summaries are capped at 250 chars by the BugCreate schema (matching the
// Jira writer's own slice); mirror it on the input so the field can never
// submit a value that would be silently shortened in Jira after review.
const SUMMARY_MAX = 250;

/**
 * Turn a FastAPI error `detail` into a readable string. For validation errors
 * `detail` is an array of {loc, msg, ...} objects, which would otherwise
 * stringify to "[object Object]"; collapse it to the joined messages instead.
 */
function formatDetail(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === "object" ? d.msg : d))
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  return fallback;
}

/**
 * Raise a Jira bug from a failed execution test case, filed under the story's
 * Epic.
 *
 * The flow is manual verify-then-create: on open we GET a draft (no Jira write)
 * and show it pre-filled and editable. The user reviews the summary/description,
 * any duplicate-candidate warnings, and whether to attach the failing step's
 * screenshots, then confirms to create the bug.
 *
 * Props:
 *   executionkey, linkkey, caseName, onClose, onCreated
 */
export default function RaiseBugModal({ executionkey, linkkey, caseName, onClose, onCreated }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(null);

  // Editable fields.
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [attachEvidence, setAttachEvidence] = useState(true);

  // Create state.
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [created, setCreated] = useState(null); // { key, url, link_error, attachments_error, attached_count }

  // Tracks whether the component is still mounted, so an in-flight draft load
  // (mount or Retry) never calls a setter after unmount.
  const mountedRef = React.useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Loading the draft is also the Retry action when the Epic lookup was
  // degraded, so it lives in a ref-stable callback that both the mount effect
  // and the Retry button can call.
  const loadDraft = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(
          executionkey
        )}/testcases/${encodeURIComponent(linkkey)}/bug-draft`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatDetail(body?.detail, `HTTP ${res.status}`));
      }
      const d = body?.data || {};
      if (mountedRef.current) {
        setDraft(d);
        setSummary(d.summary || "");
        setDescription(d.description || "");
        setAttachEvidence((d.evidence_count || 0) > 0);
      }
    } catch (e) {
      if (mountedRef.current) {
        setLoadError(e.message || "Could not load the bug draft.");
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [executionkey, linkkey]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const create = async () => {
    if (!draft) return;
    setCreating(true);
    setCreateError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(
          executionkey
        )}/testcases/${encodeURIComponent(linkkey)}/bug`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Only genuinely editable fields are sent. The Jira routing (project,
          // Epic, story, issue type, labels) is derived and validated
          // server-side from the execution test case.
          body: JSON.stringify({
            summary,
            description,
            attach_evidence: attachEvidence,
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatDetail(body?.detail, `HTTP ${res.status}`));
      }
      const data = body?.data || {};
      setCreated(data);
      onCreated?.(data);
    } catch (e) {
      setCreateError(e.message || "Could not create the bug.");
    } finally {
      setCreating(false);
    }
  };

  const epicKey = draft?.epic_key || "";
  const epicSummary = draft?.epic_summary || "";
  const storyKey = draft?.story_key || "";
  const projectKey = draft?.project_key || "";
  // Without a linked story (hence a project) the server has nowhere to file the
  // bug, so creation is blocked with a precise message instead of letting the
  // request fail validation on submit.
  const missingStory = !!draft && !(storyKey && projectKey);
  // The Epic lookup failed while composing the draft, so "no Epic" and the
  // empty duplicate set are UNVERIFIED. Block Create and offer a retry: a
  // create-time recovery could otherwise nest the bug under an Epic the user
  // never reviewed and against duplicates that were never shown. The backend
  // sets target_verified=false (with epic_lookup_error) only in this case.
  const epicLookupError = draft?.epic_lookup_error || "";
  const targetDegraded =
    !!draft && !missingStory && draft.target_verified === false;
  // The server resolves a valid, createable issue type for the project instead
  // of filing a blind "Bug" (which 400s when the project's defect type is named
  // differently). On the draft path a resolution failure is tolerated but
  // recorded here; like the degraded Epic lookup it blocks Create and offers a
  // retry, since creating would otherwise hit the same 400 the resolution hit.
  const issueType = draft?.issue_type || "";
  const issueTypeLookupError = draft?.issue_type_lookup_error || "";
  const issueTypeDegraded = !!draft && !missingStory && !!issueTypeLookupError;
  const evidenceCount = draft?.evidence_count || 0;
  const duplicates = Array.isArray(draft?.duplicate_candidates)
    ? draft.duplicate_candidates
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <Icons.Alert className="h-4 w-4 text-rose-600" />
              Raise bug
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              For failed test case{" "}
              <span className="font-medium text-slate-700">{caseName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Preparing bug draft…
            </div>
          ) : loadError ? (
            <p className="py-6 text-sm text-rose-600">{loadError}</p>
          ) : created ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
                <Icons.Check className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  Created bug{" "}
                  {created.url ? (
                    <a
                      href={created.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline"
                    >
                      {created.key}
                    </a>
                  ) : (
                    <span className="font-semibold">{created.key}</span>
                  )}
                  .
                  {created.attached_count > 0 && (
                    <span>
                      {" "}
                      {created.attached_count} screenshot
                      {created.attached_count === 1 ? "" : "s"} attached.
                    </span>
                  )}
                </div>
              </div>
              {created.link_error && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                  <span>Could not link the bug to the story: {created.link_error}</span>
                </div>
              )}
              {created.attachments_error && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                  <span>Some screenshots were not attached: {created.attachments_error}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Blocking: no linked story means no project/Epic to file against. */}
              {missingStory && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                  <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                  <span>
                    This execution isn't linked to a Jira story, so there's no
                    project or Epic to file the bug against. Link the execution to
                    its story first, then raise the bug.
                  </span>
                </div>
              )}

              {/* Target Epic (read-only), a degraded-lookup warning, or a
                  verified no-Epic warning. The degraded state is distinct from
                  "no Epic found": the lookup could not complete, so creation is
                  blocked until a retry resolves the target. */}
              {missingStory ? null : targetDegraded ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                  <div className="flex items-start gap-2">
                    <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                    <span>
                      Couldn't resolve the target Epic for story{" "}
                      <span className="font-medium">{storyKey}</span>, so the
                      Epic and any duplicate bugs could not be verified. Creation
                      is blocked until the target resolves — retry below.
                      {epicLookupError ? ` (${epicLookupError})` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={loadDraft}
                    disabled={loading}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                  >
                    {loading ? (
                      <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Retry Epic lookup
                  </button>
                </div>
              ) : epicKey ? (
                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Icons.Boards className="h-4 w-4 flex-none text-brand-600" />
                  <span className="truncate">
                    Epic: <span className="font-medium">{epicKey}</span>
                    {epicSummary ? ` — ${epicSummary}` : ""}
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                  <span>No Epic found — the bug will be created without an Epic.</span>
                </div>
              )}

              {/* Resolved issue type (read-only), or a degraded-lookup warning
                  that blocks Create. The server picks a type that actually
                  exists on the project's create screen; if resolution failed
                  while composing the draft, filing would hit the same error, so
                  Create is blocked until a retry resolves it. */}
              {missingStory ? null : issueTypeDegraded ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800">
                  <div className="flex items-start gap-2">
                    <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
                    <span>
                      Couldn't determine a valid Jira issue type for project{" "}
                      <span className="font-medium">{projectKey}</span>, so
                      creating the bug would be rejected. Creation is blocked
                      until the issue type resolves — retry below.
                      {issueTypeLookupError ? ` (${issueTypeLookupError})` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={loadDraft}
                    disabled={loading}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                  >
                    {loading ? (
                      <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Retry issue type lookup
                  </button>
                </div>
              ) : issueType ? (
                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Icons.Alert className="h-4 w-4 flex-none text-brand-600" />
                  <span className="truncate">
                    Will be created as:{" "}
                    <span className="font-medium">{issueType}</span>
                  </span>
                </div>
              ) : null}

              {/* Duplicate warnings. */}
              {duplicates.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  <div className="mb-1.5 flex items-center gap-2 font-medium">
                    <Icons.Alert className="h-4 w-4 flex-none" />
                    Possible duplicate(s) already exist — review before creating
                  </div>
                  <ul className="space-y-1 pl-6">
                    {duplicates.map((d) => (
                      <li key={d.key} className="list-disc">
                        {d.url ? (
                          <a
                            href={d.url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium underline"
                          >
                            {d.key}
                          </a>
                        ) : (
                          <span className="font-medium">{d.key}</span>
                        )}
                        {d.summary ? ` — ${d.summary}` : ""}
                        {d.status ? ` (${d.status})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Editable summary. */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Summary
                </label>
                <input
                  type="text"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  maxLength={SUMMARY_MAX}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {/* Editable description. */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {/* Story link (read-only). */}
              {storyKey && (
                <p className="text-xs text-slate-500">
                  Links to story <span className="font-medium text-slate-700">{storyKey}</span>
                </p>
              )}

              {/* Evidence toggle. */}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={attachEvidence}
                  onChange={(e) => setAttachEvidence(e.target.checked)}
                  disabled={evidenceCount === 0}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                />
                <span className={evidenceCount === 0 ? "text-slate-400" : ""}>
                  {evidenceCount} screenshot{evidenceCount === 1 ? "" : "s"} will be attached
                </span>
              </label>

              {createError && <p className="text-sm text-rose-600">{createError}</p>}
            </div>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {created ? "Close" : "Cancel"}
          </button>
          {!created && (
            <button
              type="button"
              onClick={create}
              disabled={
                creating ||
                loading ||
                !!loadError ||
                !summary.trim() ||
                missingStory ||
                targetDegraded ||
                issueTypeDegraded
              }
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating ? (
                <>
                  <Icons.Spinner className="h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Icons.Alert className="h-4 w-4" />
                  Create bug
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
